import type { ReactNode } from 'react';
import { useState } from 'react';

interface Activity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'task' | 'note';
  subject: string;
  description?: string;
  due_date?: string;
  completed: boolean;
  contact_id?: string;
  opportunity_id?: string;
  created_at: string;
}

const typeIcons: Record<Activity['type'], string> = {
  call: '📞',
  email: '✉️',
  meeting: '📅',
  task: '✅',
  note: '📝',
};

export function ActivityList(): ReactNode {
  const [activities] = useState<Activity[]>([]);
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed'>('all');
  const [typeFilter, setTypeFilter] = useState<Activity['type'] | ''>('');

  const filteredActivities = activities.filter(activity => {
    if (filter === 'pending' && activity.completed) return false;
    if (filter === 'completed' && !activity.completed) return false;
    if (typeFilter && activity.type !== typeFilter) return false;
    return true;
  });

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Activities</h1>
        <button className="btn btn-primary">+ New Activity</button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex gap-2">
            <button
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                filter === 'all' ? 'bg-ds-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                filter === 'pending' ? 'bg-ds-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setFilter('pending')}
            >
              Pending
            </button>
            <button
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                filter === 'completed' ? 'bg-ds-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
              onClick={() => setFilter('completed')}
            >
              Completed
            </button>
          </div>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as Activity['type'] | '')}
            className="px-4"
          >
            <option value="">All Types</option>
            <option value="call">Calls</option>
            <option value="email">Emails</option>
            <option value="meeting">Meetings</option>
            <option value="task">Tasks</option>
            <option value="note">Notes</option>
          </select>
        </div>
      </div>

      {/* Activity List */}
      <div className="card">
        {filteredActivities.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p className="mb-4">No activities found</p>
            <button className="btn btn-primary">Create Activity</button>
          </div>
        ) : (
          <div className="divide-y">
            {filteredActivities.map(activity => (
              <div
                key={activity.id}
                className="py-4 flex items-start gap-4 hover:bg-gray-50 -mx-4 px-4"
              >
                <span className="text-2xl">{typeIcons[activity.type]}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className={`font-medium ${activity.completed ? 'text-gray-400 line-through' : 'text-gray-900'}`}>
                      {activity.subject}
                    </h3>
                    {activity.due_date && !activity.completed && (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                        Due {new Date(activity.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  {activity.description && (
                    <p className="text-sm text-gray-500 mt-1 truncate">{activity.description}</p>
                  )}
                </div>
                <button className="btn btn-secondary text-sm">
                  {activity.completed ? 'Reopen' : 'Complete'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ActivityList;
